import { useMutation } from '@apollo/client';
import {
  namedOperations,
  RemoveRiskFromEnterpriseRiskDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { evictField } from '@/utils/graphqlUtils';

// Apollo-only: the add/remove enterprise-risk mutations are Hasura actions
// backed by the legacy REST API and have not been migrated to tRPC yet, so
// there is no flag-gated *TRPC twin. The risk read path IS flag-gated, so the
// tRPC query cache is invalidated alongside the Apollo cache.
export const useRemoveRiskFromEnterpriseRisk = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const [removeRiskFromEnterpriseRiskGraphQL, { loading, error }] = useMutation(
    RemoveRiskFromEnterpriseRiskDocument,
    {
      update: (cache) => {
        evictField(cache, 'risk');
        evictField(cache, 'risk_score');
        evictField(cache, 'enterprise_risk');
        evictField(cache, 'enterprise_risk_score');
      },
      refetchQueries: [namedOperations.Query.getRiskById],
    }
  );

  const removeRiskFromEnterpriseRisk = async (riskId: string) => {
    const result = await removeRiskFromEnterpriseRiskGraphQL({
      variables: { RiskId: riskId },
    });
    await queryClient.invalidateQueries({
      queryKey: trpc.frontend.risk.riskById.queryKey(),
    });

    return result.data;
  };

  return { removeRiskFromEnterpriseRisk, loading, error };
};
